import React, { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Loader2, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";

/**
 * Iter 720 — the page the reset email opens:
 * /reset-password?token=<jwt> (1 h, single-use).
 * POST /api/auth/reset-password { token, new_password } →
 *   200 → all sessions revoked → send the user to /login
 *   400 → link invalid/expired → offer /forgot-password.
 */
export default function ResetPasswordPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState("form"); // "form" | "done" | "expired"

  // No token in the URL → nothing to do here.
  if (!token) return <Navigate to="/forgot-password" replace />;

  const mismatch = pw2.length > 0 && pw !== pw2;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error(t("Password must be at least 8 characters."));
      return;
    }
    if (pw !== pw2) {
      toast.error(t("Passwords don't match."));
      return;
    }
    setLoading(true);
    try {
      await sharedApi.post(
        "/auth/reset-password",
        { token, new_password: pw },
        { timeout: 15000 },
      );
      setState("done");
    } catch (err) {
      if (err?.response?.status === 400 || err?.response?.status === 422) {
        // 400 = used/expired JWT; 422 = malformed token pasted into the URL.
        setState("expired");
      } else {
        toast.error(t("Couldn't reach the server — please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-12">
      <Seo title="Choose a new password" noindex />
      <div className="rounded-3xl bg-white shadow-card p-8">
        {state === "done" && (
          <div data-testid="reset-password-success">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 mb-3">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">
              {t("Password updated — please log in")}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {t("For your security, you've been signed out everywhere.")}
            </p>
            <Link
              to="/login"
              data-testid="reset-password-login-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 transition-colors"
            >
              {t("Log in")}
            </Link>
          </div>
        )}

        {state === "expired" && (
          <div data-testid="reset-password-expired">
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">
              {t("This link is invalid or has expired.")}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {t("Reset links work once and expire after 1 hour.")}
            </p>
            <Link
              to="/forgot-password"
              data-testid="reset-password-request-new"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 transition-colors"
            >
              {t("Request a new link")}
            </Link>
          </div>
        )}

        {state === "form" && (
          <>
            <h1 className="font-display text-3xl font-bold text-slate-900">
              {t("Choose a new password")}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {t("At least 8 characters. You'll be signed out everywhere after saving.")}
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="reset-password-form">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">
                  {t("New password")}
                </span>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="reset-password-new-input"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    type={show ? "text" : "password"}
                    required
                    minLength={8}
                    autoFocus
                    autoComplete="new-password"
                    className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid="reset-password-toggle-visibility"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">
                  {t("Confirm password")}
                </span>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="reset-password-confirm-input"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    type={show ? "text" : "password"}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className={`w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border focus:outline-none text-sm ${
                      mismatch
                        ? "border-red-300 focus:border-red-400"
                        : "border-soft focus:border-[color:var(--brand-blue)]"
                    }`}
                    placeholder="••••••••"
                  />
                </div>
                {mismatch && (
                  <span className="mt-1 block text-xs text-red-500" data-testid="reset-password-mismatch">
                    {t("Passwords don't match.")}
                  </span>
                )}
              </label>
              <button
                data-testid="reset-password-submit"
                type="submit"
                disabled={loading || mismatch}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {t("Save new password")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
